import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import Button from '../core/Button';
import CheckBox from '../core/Checkbox';
import Input from '../core/Input';
import { DialogWindow } from '../Dialog';
import { getPeriodStatus } from '@/helpers/periodStatus';
import { findOverlappingClaims } from '@/helpers/periodOverlap';
import {
  toPeriodDate,
  toPeriodEndTimestamp,
  toPeriodStartTimestamp,
} from '@/helpers/periodEndDate';
import { PeriodOverlapWarning } from './PeriodOverlapWarning';
import type { CollectionTicketClaimDTO } from '@/api/trafikportalen';

const periodDateOrEmpty = (
  timestamp: string | undefined,
  boundary: 'start' | 'end',
) => (timestamp ? toPeriodDate(timestamp, boundary) : '');

interface EditPeriodDialogProps {
  claim: CollectionTicketClaimDTO | null;
  onClose: () => void;
  onSave: (validFrom: string, validTo: string) => Promise<void>;
  isSaving: boolean;
  existingClaims?: CollectionTicketClaimDTO[];
}

export const EditPeriodDialog = ({
  claim,
  onClose,
  onSave,
  isSaving,
  existingClaims,
}: EditPeriodDialogProps) => {
  const { formatMessage } = useIntl();
  const today = DateTime.now().toFormat('yyyy-MM-dd');
  const tomorrow = DateTime.now().plus({ days: 1 }).toFormat('yyyy-MM-dd');

  const [formFrom, setFormFrom] = useState('');
  const [formTo, setFormTo] = useState('');
  const [endImmediately, setEndImmediately] = useState(true);

  const isUpcoming = useMemo(
    () => (claim ? getPeriodStatus(claim) === 'upcoming' : false),
    [claim],
  );

  useEffect(() => {
    if (claim) {
      setFormFrom(periodDateOrEmpty(claim.valid_from, 'start'));
      setFormTo(periodDateOrEmpty(claim.valid_to, 'end'));
    }
  }, [claim]);

  const handleOpenChange: Dispatch<SetStateAction<boolean>> = useCallback(
    (openOrUpdater: SetStateAction<boolean>) => {
      const open =
        typeof openOrUpdater === 'function'
          ? openOrUpdater(!!claim)
          : openOrUpdater;
      if (open && claim) {
        setFormFrom(periodDateOrEmpty(claim.valid_from, 'start'));
        setFormTo(periodDateOrEmpty(claim.valid_to, 'end'));
      }
      if (!open) {
        onClose();
      }
    },
    [claim, onClose],
  );

  const isFormValid = useMemo(() => {
    if (!formFrom || !formTo) return false;
    if (isUpcoming && formFrom >= formTo) return false;
    if (!isUpcoming && formFrom > formTo) return false;
    if (isUpcoming && formFrom < tomorrow) return false;
    if (!isUpcoming && formTo < today) return false;
    if (!claim) return false;
    const unchanged =
      formFrom === periodDateOrEmpty(claim.valid_from, 'start') &&
      formTo === periodDateOrEmpty(claim.valid_to, 'end');
    return !unchanged;
  }, [formFrom, formTo, today, tomorrow, claim, isUpcoming]);

  const isEndingToday = formTo === today;

  const overlappingClaims = useMemo(() => {
    if (!claim || !formFrom || !formTo || formFrom > formTo) return [];
    const from = isUpcoming
      ? toPeriodStartTimestamp(formFrom)
      : (claim.valid_from ?? toPeriodStartTimestamp(formFrom));
    return findOverlappingClaims(
      {
        from,
        to: toPeriodEndTimestamp(formTo, isEndingToday && endImmediately),
      },
      existingClaims,
      claim.id,
    );
  }, [
    claim,
    formFrom,
    formTo,
    existingClaims,
    isUpcoming,
    isEndingToday,
    endImmediately,
  ]);

  const handleSave = useCallback(async () => {
    if (!isFormValid) return;
    const validTo =
      isEndingToday && endImmediately
        ? toPeriodEndTimestamp(formTo, true)
        : formTo;
    const validFrom = isUpcoming ? formFrom : claim!.valid_from!;
    await onSave(validFrom, validTo);
  }, [
    isFormValid,
    onSave,
    formFrom,
    formTo,
    isUpcoming,
    claim,
    isEndingToday,
    endImmediately,
  ]);

  return (
    <DialogWindow
      open={!!claim}
      onOpenChange={handleOpenChange}
      title={formatMessage({ defaultMessage: 'Redigera period' })}
      className="max-w-[500px]"
      content={
        <div className="flex flex-col">
          <Input
            id="edit-period-from"
            label={formatMessage({ defaultMessage: 'Startdatum' })}
            type="date"
            value={formFrom}
            min={tomorrow}
            disabled={!isUpcoming}
            onChange={(e) => setFormFrom(e.target.value)}
            helperTextReserveSpace
          />
          <i className="mb-10 mt-2 text-text-700">
            <FormattedMessage defaultMessage="Det går inte att ändra startdatum för påbörjade perioder." />
          </i>
          <Input
            id="edit-period-to"
            label={formatMessage({ defaultMessage: 'Slutdatum' })}
            type="date"
            value={formTo}
            min={isUpcoming ? formFrom || tomorrow : today}
            onChange={(e) => setFormTo(e.target.value)}
            helperTextReserveSpace
          />
          <PeriodOverlapWarning
            overlapping={overlappingClaims}
            className="mb-4"
          />
          {isEndingToday && !isUpcoming && (
            <div className="mb-4">
              <CheckBox
                id="end-immediately"
                checked={endImmediately}
                onCheckedChange={(checked) =>
                  setEndImmediately(checked === true)
                }
                label={formatMessage({
                  defaultMessage: 'Avsluta omedelbart',
                })}
                description={formatMessage({
                  defaultMessage:
                    'Perioden avslutas direkt istället för vid midnatt.',
                })}
              />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              variant="filled"
              onClick={handleSave}
              loading={isSaving}
              disabled={!isFormValid}
            >
              <FormattedMessage defaultMessage="Spara" />
            </Button>
          </div>
        </div>
      }
    />
  );
};
